import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { RefreshCw, Loader2, Clock, CheckCircle2, XCircle, AlertTriangle, MessageSquareReply, Inbox } from 'lucide-react';

/**
 * MonitoreoTab — panel SOLO-LECTURA del módulo de Lógicas. Lee GET /comm-logic-recipes/monitoreo
 * al montar y al pulsar "Actualizar". NO hace polling ni corre nada en background.
 */

// Fecha ISO → "27 jun 13:16" en hora de México (legible, no ISO crudo).
const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-MX', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
      timeZone: 'America/Mexico_City', hour12: false,
    });
  } catch {
    return String(iso);
  }
};

const METRICAS = [
  { key: 'en_cola', label: 'En cola', Icon: Inbox, color: 'text-blue-400' },
  { key: 'entregados_hoy', label: 'Entregados hoy', Icon: CheckCircle2, color: 'text-emerald-400' },
  { key: 'fallaron_hoy', label: 'Fallaron hoy', Icon: XCircle, color: 'text-red-400' },
  { key: 'atascados', label: 'Atascados', Icon: AlertTriangle, color: 'text-amber-400' },
  { key: 'respondieron', label: 'Respondieron al seguimiento', Icon: MessageSquareReply, color: 'text-violet-400' },
];

export default function MonitoreoTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const r = await api.get('/comm-logic-recipes/monitoreo');
      setData(r.data);
    } catch {
      setError(true);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const m = data?.metricas || {};
  const atascados = data?.atascados || [];
  const noEntregados = data?.no_entregados || [];
  const proximos = data?.proximos || [];

  return (
    <div className="space-y-5" data-testid="monitoreo-tab">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] text-muted-foreground">Lectura al abrir o al pulsar Actualizar (no es en vivo).</p>
        <Button variant="outline" size="sm" className="text-xs h-7" onClick={load} disabled={loading} data-testid="monitoreo-refresh">
          {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}Actualizar
        </Button>
      </div>

      {error ? (
        <Card className="bg-card/50 border-white/5"><CardContent className="py-10 text-center space-y-3">
          <p className="text-sm text-white">No se pudo cargar el monitoreo.</p>
          <Button variant="outline" size="sm" className="text-xs" onClick={load}>Reintentar</Button>
        </CardContent></Card>
      ) : loading && !data ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <>
          {/* (a) 5 tarjetas de métricas */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {METRICAS.map(({ key, label, Icon, color }) => (
              <Card key={key} className="bg-card/50 border-white/5" data-testid={`metrica-${key}`}>
                <CardContent className="p-3 space-y-1">
                  <div className="flex items-center gap-1.5">
                    <Icon className={`w-3.5 h-3.5 ${color} shrink-0`} />
                    <span className="text-[10px] text-muted-foreground leading-tight">{label}</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{m[key] ?? 0}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* (b) Atascados */}
          <div className="space-y-2">
            <p className="text-[11px] font-semibold text-white flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-amber-400" />Atascados — llevan más de 1 h en cola</p>
            {atascados.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">Nada atascado. Todo fluye al día.</p>
            ) : (
              <div className="space-y-1.5">
                {atascados.map((c) => (
                  <Card key={c.id} className="bg-amber-500/5 border-amber-500/15"><CardContent className="flex items-center justify-between gap-2 px-3 py-2 text-xs">
                    <span className="text-white">{c.lead_name}{c.lead_phone ? ' · ' + c.lead_phone : ''} · paso {(c.current_step ?? 0) + 1}</span>
                    <span className="text-muted-foreground text-right">debía salir {fmt(c.next_check_at)} · {c.status}</span>
                  </CardContent></Card>
                ))}
              </div>
            )}
          </div>

          {/* (c) No entregados, últimos 7 días, por razón */}
          <div className="space-y-2">
            <p className="text-[11px] font-semibold text-white flex items-center gap-1.5"><XCircle className="w-3.5 h-3.5 text-red-400" />No entregados — últimos 7 días, por razón</p>
            {noEntregados.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">Sin fallos de entrega en los últimos 7 días.</p>
            ) : (
              <div className="space-y-1.5">
                {noEntregados.map((r, i) => (
                  <Card key={i} className="bg-card/50 border-white/5"><CardContent className="flex items-center justify-between gap-2 px-3 py-2 text-xs">
                    <span className="text-zinc-200">{r.razon_clara}{r.error_code_ejemplo ? <span className="text-muted-foreground"> · cód {r.error_code_ejemplo}</span> : null}</span>
                    <span className="text-white font-semibold shrink-0">{r.count}</span>
                  </CardContent></Card>
                ))}
              </div>
            )}
          </div>

          {/* (d) Próximos a salir */}
          <div className="space-y-2">
            <p className="text-[11px] font-semibold text-white flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-blue-400" />Próximos a salir</p>
            {proximos.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">No hay seguimientos en cola por ahora.</p>
            ) : (
              <div className="space-y-1.5">
                {proximos.map((c) => (
                  <Card key={c.id} className="bg-card/50 border-white/5"><CardContent className="flex items-center justify-between gap-2 px-3 py-2 text-xs">
                    <span className="text-white">{c.lead_name}{c.lead_phone ? ' · ' + c.lead_phone : ''} · paso {(c.current_step ?? 0) + 1}</span>
                    <span className="text-muted-foreground">sale {fmt(c.next_check_at)}</span>
                  </CardContent></Card>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
